import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, getApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from Emergent OAuth callback, skip /me check.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password, totpCode) => {
    try {
      const payload = { email, password };
      if (totpCode) payload.totp_code = totpCode;
      const { data } = await api.post("/auth/login", payload);
      if (data.requires_2fa) return { ok: false, requires_2fa: true };
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: getApiError(e) };
    }
  };

  const register = async (name, email, password, role_intent = "homeowner") => {
    try {
      const { data } = await api.post("/auth/register", { name, email, password, role_intent });
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: getApiError(e) };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // eslint-disable-next-line no-console -- logout failures shouldn't block UI but are useful for debugging
      console.warn("Logout request failed (clearing local session anyway)", e);
    }
    setUser(null);
  };

  const refreshMe = fetchMe;

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshMe, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
